import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { useMutation } from '@apollo/react-hooks'
import { yupResolver } from '@hookform/resolvers'
import { Col, Collapse, Form as AntForm, Icon, Modal, Row } from 'antd'
import {
  every,
  filter,
  find,
  flatMap,
  has,
  includes,
  isEmpty,
  keys,
  map,
  mergeWith,
  range,
  size,
  slice,
  uniqBy,
  values,
} from 'lodash-es'
import PropTypes from 'prop-types'

import {
  Breadcrumb,
  Checkbox,
  FormDate,
  FormInternalLink,
  FormText,
  HiddenCheckboxes,
  HiddenInputs,
  Input,
  Select,
} from '../../../core/components'
import { Button, Panel } from '../../../core/components/styled'
import {
  createUrl,
  getQueryParams,
} from '../../../core/utils/services/queryParams'

import {
  HistoryDrawer,
  LockedStatus,
  OpenLink,
  Spinner,
  VisibilityContainer,
} from '../../../common/components'
import {
  PageActions,
  PageHeader,
  PageTitle,
} from '../../../common/components/styled'
import {
  ADMIN,
  BRAND,
  BRAND_GATEWAY_TASK,
  BRAND_GATEWAY_TASKS_PERMISSIONS,
  DEFAULT_PAGE_SIZE,
  TEAM_LEADER,
} from '../../../common/constants'
import {
  showErrorNotification,
  showSuccessNotification,
  showWarningNotification,
} from '../../../common/helpers'
import { useIsLoggedInUser, useUserHasRoles } from '../../../common/hooks'
import TaskResolveMessage from '../common/TaskResolveMessage'

import { AuthContext } from '../../../account/contexts'
import {
  BrandsDropdown,
  CreateBrandModal,
} from '../../../brands/components/pages/sections'
import {
  ATLAS_IMPORT_SPECIFIC_EXCLUSION_REASONS,
  BRAND_DEFINITION_TASK_SPECIFIC_EXCLUSION_REASONS,
  BRAND_EXCLUSION_REASONS,
  BRAND_RANKS,
  BRAND_STATUSES,
  EXCLUDED,
  INACCESSIBLE_URL,
  INCLUDED,
  MASTER,
  MISC,
  NEW,
  SLAVE,
} from '../../../brands/constants'
import { NO_INCLUDED_LOCATIONS } from '../../../brands/constants/brand-exclusion-reasons'
import {
  BRAND_GATEWAY,
  DONE,
  MANUAL_CHANGE,
  QQA,
  REOPENED,
  RESOLVED,
  TASK_AWAITING_REASONS,
  TASK_REOPEN_REASONS,
  UPDATE,
} from '../../constants'
import {
  ASSIGN_BRAND_GATEWAY_TASK,
  MARK_BRAND_GATEWAY_TASK_AS_AWAITING,
  MARK_BRAND_GATEWAY_TASK_MISC_AS_QC_VERIFIED,
  MARK_BRAND_GATEWAY_TASK_MISC_AS_VERIFIED,
  REOPEN_BRAND_GATEWAY_TASK,
  UNASSIGN_BRAND_GATEWAY_TASK,
  UNMARK_BRAND_GATEWAY_TASK_AS_AWAITING,
} from '../../graphql/mutations'
import {
  getBreadcrumbItemsForBrandGatewayTaskPage,
  getTaskActionButtonColors,
} from '../../helpers'
import { mergeFormValues } from '../../utils'
import {
  AwaitingReasonField,
  BrandExclusionCommentModal,
  BrandGatewayLocations,
  BrandGatewayRelatedBrands,
  BrandGatewaySimilarBrandsByLocationWebsite,
  BrandGatewaySimilarBrandsByName,
  BrandGatewaySimilarMasterBrandsByLocationWebsite,
  BrandGatewayTaskBrandExclusionReasonComment,
  MarkMiscTaskAsVerifiedModal,
  MiscTaskVerificationThreeStateSwitch,
  TaskActionSection,
  TaskViewsStatus,
} from '../pages/sections'
import BrandGatewaySimilarMasterBrandsByName from '../pages/sections/brand-gateway-similar-master-brands-by-name'

import { brandGatewayTaskState } from './global-states'
import { brandGatewayTaskSchema } from './schemas'
import TaskViewBanner from './task-view-banner'
import { brandGatewayTaskStateValidator } from './validators'

const inclusionOptions = [
  { name: BRAND_STATUSES.INCLUDED.name, value: BRAND_STATUSES.INCLUDED.value },
  { name: BRAND_STATUSES.EXCLUDED.name, value: BRAND_STATUSES.EXCLUDED.value },
]

const filterRelatedBrandsBySearchTerm = (relatedBrands, searchTerm) => {
  if (!searchTerm) return relatedBrands

  const isNumericBrandId = /^\d+$/.test(searchTerm.trim())
  const searchLower = searchTerm.toLowerCase()

  return filter(relatedBrands, brand => {
    if (isNumericBrandId) {
      return brand.id === searchTerm.trim()
    }
    return brand.name.toLowerCase().includes(searchLower)
  })
}

const getFormState = (
  taskState,
  locationsPage,
  locationsPageSize = DEFAULT_PAGE_SIZE,
  relatedBrandsPage,
  filterOriginalBrandId,
  filterLocationId,
  relatedBrandsSearchTerm,
) => {
  let { locations } = taskState

  if (filterLocationId) {
    locations = filter(locations, ({ id }) => String(id) === filterLocationId)
  } else if (filterOriginalBrandId) {
    locations = filter(
      locations,
      ({ originalBrand }) => originalBrand.id === filterOriginalBrandId,
    )
  }

  const filteredRelatedBrands = filterRelatedBrandsBySearchTerm(
    taskState.relatedBrands,
    relatedBrandsSearchTerm,
  )

  return {
    ...taskState,
    locations: slice(
      locations,
      (locationsPage - 1) * locationsPageSize,
      (locationsPage - 1) * locationsPageSize + locationsPageSize,
    ),
    filteredLocationCount: size(locations),
    relatedBrands: slice(
      filteredRelatedBrands,
      (relatedBrandsPage - 1) * DEFAULT_PAGE_SIZE,
      (relatedBrandsPage - 1) * DEFAULT_PAGE_SIZE + DEFAULT_PAGE_SIZE,
    ),
    filteredRelatedBrandCount: size(filteredRelatedBrands),
  }
}

const recalculateFormValues = ({
  getValues,
  taskState,
  setTaskState,
  locationsPage,
  locationsPageSize,
  relatedBrandsPage,
  reset,
  filterOriginalBrandId,
  setFilteredLocationCount,
  filterLocationId,
  relatedBrandsSearchTerm,
}) => {
  const formValues = getValues()
  const updatedTaskState = mergeWith(taskState, formValues, mergeFormValues)
  setTaskState(updatedTaskState)

  const filteredRelatedBrands = filterRelatedBrandsBySearchTerm(
    taskState.relatedBrands,
    relatedBrandsSearchTerm,
  )

  if (filterLocationId || filterOriginalBrandId) {
    const filteredLocations = filterLocationId
      ? filter(taskState.locations, ({ id }) => String(id) === filterLocationId)
      : filter(
          taskState.locations,
          ({ originalBrand }) => originalBrand.id === filterOriginalBrandId,
        )

    const formState = {
      ...taskState,
      locations: slice(
        filteredLocations,
        (locationsPage - 1) * locationsPageSize,
        (locationsPage - 1) * locationsPageSize + locationsPageSize,
      ),
      relatedBrands: slice(
        filteredRelatedBrands,
        (relatedBrandsPage - 1) * DEFAULT_PAGE_SIZE,
        (relatedBrandsPage - 1) * DEFAULT_PAGE_SIZE + DEFAULT_PAGE_SIZE,
      ),
      filteredRelatedBrandCount: size(filteredRelatedBrands),
    }

    reset(formState)
    setFilteredLocationCount(size(filteredLocations))
  } else {
    const formState = getFormState(
      updatedTaskState,
      locationsPage,
      locationsPageSize,
      relatedBrandsPage,
      filterOriginalBrandId,
      filterLocationId,
      relatedBrandsSearchTerm,
    )
    setFilteredLocationCount(size(taskState.locations))
    reset(formState)
  }
}

const getOriginalBrands = ({ locations }) =>
  uniqBy(map(locations, 'originalBrand'), 'id')

const BrandGatewayTaskForm = ({
  task,
  isRefetchingTask,
  isLoadingSimilarBrands,
  similarBrands,
  refetchSimilarBrands,
  isLoadingSimilarMasterBrandsByName,
  similarMasterBrandsByName,
  refetchSimilarMasterBrandsByName,
  isLoadingSimilarMasterBrandsByLocationWebsite,
  similarMasterBrandsByLocationWebsite,
  refetchSimilarMasterBrandsByLocationWebsite,
  isSavingTask,
  isResolvingTask,
  userLockingBrand,
  onSubmit,
}) => {
  const currentView = useMemo(
    () => find(task.views, view => task.currentViewType === view.type),
    [task.views, task.currentViewType],
  )

  const shouldHighlightField = fieldName =>
    currentView.status === UPDATE &&
    includes(currentView.modifiedBrandFields, fieldName)

  const history = useHistory()
  const { pathname, search } = useLocation()
  const {
    fromSlaveBrandId,
    locationsPageSize = DEFAULT_PAGE_SIZE,
  } = getQueryParams(search)

  const setLocationsPageSize = useCallback(
    locationsPageSize =>
      history.push(createUrl(pathname, search, { locationsPageSize })),
    [history, search, pathname],
  )

  const { user: loggedInUser } = useContext(AuthContext)

  const [locationsPage, setLocationsPage] = useState(1)
  const [relatedBrandsPage, setRelatedBrandsPage] = useState(1)
  const [relatedBrandsSearchTerm, setRelatedBrandsSearchTerm] = useState('')
  const [filterOriginalBrandId, setFilterOriginalBrandId] = useState(
    fromSlaveBrandId && String(fromSlaveBrandId),
  )
  const [allLocationsStatus, setAllLocationsStatus] = useState()
  const [filterLocationId, setFilterLocationId] = useState('')

  const [cachedBrandWebsiteValue, setCachedBrandWebsiteValue] = useState(
    task.brand.website,
  )

  const [isExclusionReasonModalOpen, setIsExclusionCommentModalOpen] = useState(
    false,
  )

  const [isCreateBrandModalOpen, setIsCreateBrandModalOpen] = useState(false)

  const [isQCVerified, setIsQCVerified] = useState(false)

  const shouldShowReopenButton = useMemo(
    () => currentView.type === QQA && currentView.status === DONE,
    [currentView.type, currentView.status],
  )

  const [
    isMarkMiscTaskAsVerifiedModalOpen,
    setIsMarkMiscTaskAsVerifiedModalOpen,
  ] = useState(false)

  const taskStateCurrentValues = useMemo(
    () => brandGatewayTaskState(task, currentView, similarBrands),
    [task, currentView, similarBrands],
  )

  const [taskState, setTaskState] = useState(taskStateCurrentValues)

  const [filteredLocationCount, setFilteredLocationCount] = useState(
    size(taskState.locations),
  )

  const formMethods = useForm({
    defaultValues: getFormState(
      taskState,
      locationsPage,
      locationsPageSize,
      relatedBrandsPage,
      filterOriginalBrandId,
      filterLocationId,
    ),
    resolver: yupResolver(brandGatewayTaskSchema),
  })

  const {
    handleSubmit,
    setValue,
    getValues,
    watch,
    reset,
    errors,
    formState: { dirtyFields, isSubmitted },
  } = formMethods

  useEffect(() => {
    const { task, brand: formBrand } = getValues()

    const {
      locations,
      filteredLocationCount,
      relatedBrands,
      similarBrands,
      brand: taskStateBrand,
    } = getFormState(
      taskStateCurrentValues,
      1,
      locationsPageSize,
      1,
      filterOriginalBrandId,
    )

    const formValues = {
      task,
      brand: {
        ...taskStateBrand,
        name: formBrand.name,
        website: formBrand.website,
        isHasNoWebsiteConfirmed: formBrand.isHasNoWebsiteConfirmed,
        rank: formBrand.rank,
        status:
          taskStateBrand.exclusionReason === NO_INCLUDED_LOCATIONS
            ? taskStateBrand.status
            : formBrand.status,
        exclusionReason:
          taskStateBrand.exclusionReason === NO_INCLUDED_LOCATIONS
            ? taskStateBrand.exclusionReason
            : formBrand.exclusionReason,
        exclusionReasonComment:
          taskStateBrand.exclusionReason === NO_INCLUDED_LOCATIONS
            ? taskStateBrand.exclusionReasonComment
            : formBrand.exclusionReasonComment,
      },
      locations,
      relatedBrands,
      similarBrands,
    }

    setTaskState(taskStateCurrentValues)
    setLocationsPage(1)
    setFilteredLocationCount(filteredLocationCount)
    setRelatedBrandsPage(1)
    setRelatedBrandsSearchTerm('')
    setAllLocationsStatus()
    setFilterLocationId('')
    reset(formValues)
  }, [
    getValues,
    taskState,
    similarBrands,
    reset,
    taskStateCurrentValues,
    fromSlaveBrandId,
    filterOriginalBrandId,
    locationsPageSize,
  ])

  const [assignTask, { loading: isAssigningTask }] = useMutation(
    ASSIGN_BRAND_GATEWAY_TASK,
    {
      onCompleted({ assignBrandGatewayTask }) {
        const { task } = getValues()

        const updatedTask = {
          ...task,
          modifiedDate: assignBrandGatewayTask.modifiedDate,
          isAssigned: true,
          assignedTo: {
            id: loggedInUser.id,
            name: loggedInUser.name,
          },
        }

        setValue('task', updatedTask)

        showSuccessNotification({
          message: 'Brand gateway task assigned.',
        })
      },
      onError(error) {
        showErrorNotification({
          message: 'Brand gateway assign failed.',
          error,
        })
      },
    },
  )

  const [unassignTask, { loading: isUnassigningTask }] = useMutation(
    UNASSIGN_BRAND_GATEWAY_TASK,
    {
      onCompleted({ unassignBrandGatewayTask }) {
        const { task } = getValues()

        const updatedTask = {
          ...task,
          modifiedDate: unassignBrandGatewayTask.modifiedDate,
          isAssigned: false,
          assignedTo: {
            id: '',
            name: '',
          },
        }

        setValue('task', updatedTask)

        showSuccessNotification({
          message: 'Brand gateway task unassigned.',
        })
      },
      onError({ message }) {
        showErrorNotification({
          message: 'Brand gateway task unassign failed.',
          description: message,
        })
      },
    },
  )

  const [
    markTaskAsAwaiting,
    { loading: isMarkingTaskAsAwaiting },
  ] = useMutation(MARK_BRAND_GATEWAY_TASK_AS_AWAITING, {
    onCompleted({ markBrandGatewayTaskAsAwaiting }) {
      const { task } = getValues()

      const updatedTask = {
        ...task,
        modifiedDate: markBrandGatewayTaskAsAwaiting.modifiedDate,
        isAwaiting: true,
        awaitingReason:
          TASK_AWAITING_REASONS[markBrandGatewayTaskAsAwaiting.awaitingReason]
            .name,
        awaitingReasonComment:
          markBrandGatewayTaskAsAwaiting.awaitingReasonComment,
      }

      setValue('task', updatedTask)

      showSuccessNotification({
        message: 'Brand gateway task marked as awaiting.',
      })
    },
    onError({ message }) {
      showErrorNotification({
        message: 'Brand gateway task mark as awaiting failed.',
        description: message,
      })
    },
  })

  const [
    unmarkTaskAsAwaiting,
    { loading: isUnmarkingTaskAsAwaiting },
  ] = useMutation(UNMARK_BRAND_GATEWAY_TASK_AS_AWAITING, {
    onCompleted({ unmarkBrandGatewayTaskAsAwaiting }) {
      const { task } = getValues()

      const updatedTask = {
        ...task,
        modifiedDate: unmarkBrandGatewayTaskAsAwaiting.modifiedDate,
        isAwaiting: false,
        awaitingReason: '',
        awaitingReasonComment: '',
      }

      setValue('task', updatedTask)

      showSuccessNotification({
        message: 'Brand gateway task unmarked as awaiting.',
      })
    },
    onError({ message }) {
      showErrorNotification({
        message: 'Brand gateway task unmark as awaiting failed.',
        description: message,
      })
    },
  })

  const [reopenTask, { loading: isReopeningTask }] = useMutation(
    REOPEN_BRAND_GATEWAY_TASK,
    {
      onCompleted({
        reopenBrandGatewayTask: {
          modifiedDate,
          reopenedDate,
          assignedTo,
          currentViewType,
          views,
        },
      }) {
        const { task } = getValues()

        const updatedTask = {
          ...task,
          isAssigned: !!assignedTo,
          assignedTo: {
            id: assignedTo?.id,
            name: assignedTo?.name,
          },
          modifiedDate,
          reopenedBy: {
            id: loggedInUser.id,
            name: loggedInUser.name,
          },
          reopenedDate,
          reopenReason: TASK_REOPEN_REASONS[MANUAL_CHANGE].name,
          status: REOPENED,
          currentViewType,
          views,
        }

        setValue('task', updatedTask)

        showSuccessNotification({
          message: 'Brand gateway task reopened.',
        })
      },
      onError(error) {
        showErrorNotification({
          message: 'Brand gateway task reopen failed.',
          error,
        })
      },
    },
  )

  const [
    markMiscTaskAsVerified,
    { loading: isMarkingMiscTaskAsVerified },
  ] = useMutation(MARK_BRAND_GATEWAY_TASK_MISC_AS_VERIFIED, {
    onCompleted({
      markBrandGatewayTaskMiscAsVerified: {
        miscTaskVerifiedDate,
        miscTaskVerifiedComment,
      },
    }) {
      const { task } = getValues()

      const updatedTask = {
        ...task,
        miscTaskVerifiedBy: {
          id: loggedInUser.id,
          name: loggedInUser.name,
        },
        miscTaskVerifiedDate,
        miscTaskVerifiedComment,
      }

      setValue('task', updatedTask)

      showSuccessNotification({
        message: 'Miscellaneous task marked as verified.',
      })
    },
    onError({ message }) {
      showErrorNotification({
        message: 'Miscellaneous task mark as verified failed.',
        description: message,
      })
    },
  })

  const [
    markMiscTaskAsQCVerified,
    { loading: isMarkingMiscTaskAsQCVerified },
  ] = useMutation(MARK_BRAND_GATEWAY_TASK_MISC_AS_QC_VERIFIED, {
    onCompleted({
      markBrandGatewayTaskMiscAsQCVerified: {
        miscTaskQCVerifiedDate,
        miscTaskQCVerifiedComment,
      },
    }) {
      const { task } = getValues()

      const updatedTask = {
        ...task,
        miscTaskQCVerifiedBy: {
          id: loggedInUser.id,
          name: loggedInUser.name,
        },
        miscTaskQCVerifiedDate,
        miscTaskQCVerifiedComment,
      }

      setValue('task', updatedTask)

      showSuccessNotification({
        message: 'Miscellaneous task marked as QC verified.',
      })
    },
    onError({ message }) {
      showErrorNotification({
        message: 'Miscellaneous task mark as QC verified failed.',
        description: message,
      })
    },
  })

  const onAssignTask = useCallback(
    () =>
      assignTask({
        variables: { id: task.id, toUserId: loggedInUser.id },
      }),
    [assignTask, task, loggedInUser],
  )

  const onUnassignTask = useCallback(
    () => unassignTask({ variables: { id: task.id } }),
    [unassignTask, task],
  )

  const onSave = useCallback(async () => {
    setValue('task.isDone', false)
    handleSubmit(onSubmit)()
  }, [setValue, handleSubmit, onSubmit])

  const onDone = useCallback(async () => {
    setValue('task.isDone', true)
    handleSubmit(async formValues => {
      const updatedTaskState = mergeWith(taskState, formValues, mergeFormValues)
      const isValid = await brandGatewayTaskStateValidator(updatedTaskState)

      if (isValid) {
        onSubmit(updatedTaskState)
      }
    })()
  }, [setValue, taskState, handleSubmit, onSubmit])

  const onMarkTaskAsAwaiting = useCallback(
    awaitingReasonData =>
      markTaskAsAwaiting({
        variables: { id: task.id, ...awaitingReasonData },
      }),
    [markTaskAsAwaiting, task],
  )

  const onUnmarkTaskAsAwaiting = useCallback(() => {
    unmarkTaskAsAwaiting({ variables: { id: task.id } })
  }, [unmarkTaskAsAwaiting, task])

  const onVerifiedButtonClick = () => {
    setIsQCVerified(false)
    setIsMarkMiscTaskAsVerifiedModalOpen(true)
  }

  const onQCVerifiedButtonClick = () => {
    setIsQCVerified(true)
    setIsMarkMiscTaskAsVerifiedModalOpen(true)
  }

  const handleMarkMiscTaskAsVerified = useCallback(
    ({ comment }) => {
      const data = { variables: { id: task.id, comment } }
      setIsMarkMiscTaskAsVerifiedModalOpen(false)

      return isQCVerified
        ? markMiscTaskAsQCVerified(data)
        : markMiscTaskAsVerified(data)
    },
    [isQCVerified, markMiscTaskAsVerified, markMiscTaskAsQCVerified, task],
  )

  const onReopenTask = useCallback(
    () => reopenTask({ variables: { id: task.id } }),
    [reopenTask, task],
  )

  const onExclusionCommentSubmit = ({ exclusionComment }) => {
    setValue('brand.exclusionReasonComment', exclusionComment)
    setIsExclusionCommentModalOpen(false)
  }

  const onLocationsPageChange = useCallback(
    (locationsPage, locationsPageSize) => {
      const recalculate = () => {
        recalculateFormValues({
          getValues,
          taskState,
          setTaskState,
          locationsPage,
          locationsPageSize,
          relatedBrandsPage,
          reset,
          filterOriginalBrandId,
          setFilteredLocationCount,
          filterLocationId,
          relatedBrandsSearchTerm,
        })
        setAllLocationsStatus()
        setLocationsPage(locationsPage)
        setLocationsPageSize(locationsPageSize)
      }

      if (includes(keys(dirtyFields), 'locations') && !isSubmitted) {
        Modal.confirm({
          title: 'Unsaved changes',
          content:
            'There are locations that are not saved. Do you want to continue?',
          onOk: recalculate,
        })
      } else {
        recalculate()
      }
    },
    [
      dirtyFields,
      isSubmitted,
      getValues,
      relatedBrandsPage,
      reset,
      taskState,
      filterOriginalBrandId,
      setLocationsPageSize,
      filterLocationId,
      relatedBrandsSearchTerm,
    ],
  )

  const onRelatedBrandsPageChange = useCallback(
    relatedBrandsPage => {
      const recalculate = () => {
        recalculateFormValues({
          getValues,
          taskState,
          setTaskState,
          locationsPage,
          locationsPageSize,
          relatedBrandsPage,
          reset,
          filterOriginalBrandId,
          setFilteredLocationCount,
          filterLocationId,
          relatedBrandsSearchTerm,
        })
        setRelatedBrandsPage(relatedBrandsPage)
      }

      if (includes(keys(dirtyFields), 'relatedBrands') && !isSubmitted) {
        Modal.confirm({
          title: 'Unsaved changes',
          content:
            'There are related brands that are not saved. Do you want to continue?',
          onOk: recalculate,
        })
      } else {
        recalculate()
      }
    },
    [
      dirtyFields,
      isSubmitted,
      getValues,
      taskState,
      locationsPage,
      locationsPageSize,
      reset,
      filterOriginalBrandId,
      filterLocationId,
      relatedBrandsSearchTerm,
    ],
  )

  const onRelatedBrandsSearchChange = useCallback(
    searchTerm => {
      const recalculate = () => {
        setRelatedBrandsSearchTerm(searchTerm)
        setRelatedBrandsPage(1)
        recalculateFormValues({
          getValues,
          taskState,
          setTaskState,
          locationsPage,
          locationsPageSize,
          relatedBrandsPage: 1,
          reset,
          filterOriginalBrandId,
          setFilteredLocationCount,
          filterLocationId,
          relatedBrandsSearchTerm: searchTerm,
        })
      }

      if (includes(keys(dirtyFields), 'relatedBrands') && !isSubmitted) {
        Modal.confirm({
          title: 'Unsaved changes',
          content:
            'There are related brands that are not saved. Do you want to continue?',
          onOk: recalculate,
        })
      } else {
        recalculate()
      }
    },
    [
      dirtyFields,
      isSubmitted,
      getValues,
      taskState,
      locationsPage,
      locationsPageSize,
      reset,
      filterOriginalBrandId,
      filterLocationId,
    ],
  )

  const onSelectOriginalBrandChange = useCallback(
    brandId => {
      const applyFilter = () => {
        history.push(createUrl(pathname))

        setLocationsPage(1)
        setAllLocationsStatus()
        setFilterOriginalBrandId(brandId)
        setFilterLocationId('')
        recalculateFormValues({
          getValues,
          taskState,
          setTaskState,
          locationsPage: 1,
          locationsPageSize: DEFAULT_PAGE_SIZE,
          relatedBrandsPage,
          reset,
          filterOriginalBrandId: brandId,
          setFilteredLocationCount,
          filterLocationId: '',
          relatedBrandsSearchTerm,
        })
      }

      if (includes(keys(dirtyFields), 'locations') && !isSubmitted) {
        Modal.confirm({
          title: 'Unsaved changes',
          content:
            'There are locations that are not saved. Do you want to continue?',
          onOk: applyFilter,
        })
      } else {
        applyFilter()
      }
    },
    [
      dirtyFields,
      isSubmitted,
      getValues,
      relatedBrandsPage,
      reset,
      taskState,
      history,
      pathname,
      relatedBrandsSearchTerm,
    ],
  )

  const onFilterLocationIdChange = useCallback(
    searchValue => {
      const applySearch = () => {
        setFilterLocationId(searchValue)
        setFilterOriginalBrandId(undefined)
        setLocationsPage(1)
        setAllLocationsStatus()
        recalculateFormValues({
          getValues,
          taskState,
          setTaskState,
          locationsPage: 1,
          locationsPageSize,
          relatedBrandsPage,
          reset,
          filterOriginalBrandId: undefined,
          setFilteredLocationCount,
          filterLocationId: searchValue,
          relatedBrandsSearchTerm,
        })
      }

      if (includes(keys(dirtyFields), 'locations') && !isSubmitted) {
        Modal.confirm({
          title: 'Unsaved changes',
          content:
            'There are locations that are not saved. Do you want to continue?',
          onOk: applySearch,
        })
      } else {
        applySearch()
      }
    },
    [
      dirtyFields,
      isSubmitted,
      getValues,
      taskState,
      locationsPageSize,
      relatedBrandsPage,
      reset,
      setFilteredLocationCount,
      relatedBrandsSearchTerm,
    ],
  )

  const brandRank = watch('brand.rank')
  const brandStatus = watch('brand.status')
  const masterBrand = watch('brand.masterBrand')
  const taskStatus = watch('task.status')
  const miscTaskVerifiedComment = watch('task.miscTaskVerifiedComment')
  const miscTaskQCVerifiedComment = watch('task.miscTaskQCVerifiedComment')
  const currentViewType = watch('task.currentViewType')
  const assignedTo = watch('task.assignedTo')

  const isLoggedInUser = useIsLoggedInUser(assignedTo)
  const isAdmin = useUserHasRoles(ADMIN)
  const isTeamLeader = useUserHasRoles(TEAM_LEADER)

  const breadcrumbItems = useMemo(
    () => getBreadcrumbItemsForBrandGatewayTaskPage(task),
    [task],
  )

  const shouldShowBreadcrumb = useMemo(() => {
    const { status, locations } = task.brand

    if (status === INCLUDED) return true

    const isExcludedWithBrandDefinitionExclusionReason = every(
      locations,
      location =>
        includes(
          [
            ...BRAND_DEFINITION_TASK_SPECIFIC_EXCLUSION_REASONS,
            ...ATLAS_IMPORT_SPECIFIC_EXCLUSION_REASONS,
          ],
          location.exclusionReason,
        ),
    )

    if (isExcludedWithBrandDefinitionExclusionReason) return true

    return false
  }, [task.brand])

  return (
    <>
      <TaskViewBanner currentViewType={currentViewType} />
      {(isAssigningTask ||
        isUnassigningTask ||
        isSavingTask ||
        isResolvingTask ||
        isMarkingTaskAsAwaiting ||
        isUnmarkingTaskAsAwaiting ||
        isReopeningTask ||
        isRefetchingTask) && <Spinner size="large" />}

      <VisibilityContainer isHidden={!shouldShowBreadcrumb}>
        <Breadcrumb currentTaskType={BRAND_GATEWAY} items={breadcrumbItems} />
      </VisibilityContainer>

      <PageHeader sticky style={{ top: 40 }}>
        <PageTitle>
          Brand gateway task
          <LockedStatus
            userLockingEntity={userLockingBrand}
            entityType={BRAND}
            margin="no no small xxSmall"
          />
          <HistoryDrawer entityType={BRAND_GATEWAY_TASK} entityId={task.id} />
          <TaskViewsStatus task={watch('task')} />
        </PageTitle>
        <PageActions>
          <TaskActionSection
            task={watch('task')}
            taskType={BRAND_GATEWAY}
            permissions={BRAND_GATEWAY_TASKS_PERMISSIONS}
            onAssignTask={onAssignTask}
            onUnassignTask={onUnassignTask}
            onSave={onSave}
            onDone={onDone}
            onMarkTaskAsAwaiting={onMarkTaskAsAwaiting}
            onUnmarkTaskAsAwaiting={onUnmarkTaskAsAwaiting}
            onReopenTask={shouldShowReopenButton && onReopenTask}
            cancelUrl="/tasks/brand-gateway"
            doneButtonTitle={
              <TaskResolveMessage currentViewType={currentViewType} />
            }
            buttonColors={getTaskActionButtonColors(currentViewType)}
          />
        </PageActions>
      </PageHeader>

      <FormProvider {...formMethods}>
        <form>
          <Collapse defaultActiveKey={['brand-section', 'task-section']}>
            <Collapse.Panel
              header="Brand"
              key="brand-section"
              style={has(errors, 'brand') ? { border: '1px solid red' } : {}}
            >
              <Row gutter={16}>
                <Col span={2}>
                  <FormInternalLink
                    name="brand.id"
                    label="ID"
                    transform={id => `/brands/${id}`}
                  />
                </Col>
                <Col span={3}>
                  <Input
                    name="brand.name"
                    label="Name"
                    highlighted={shouldHighlightField('name')}
                  />
                </Col>
                <Col span={4}>
                  <Input
                    name="brand.website"
                    label="Website"
                    disabled={watch('brand.isHasNoWebsiteConfirmed')}
                    highlighted={shouldHighlightField('website')}
                    addonAfter={
                      <OpenLink
                        onClick={() =>
                          !watch('brand.isHasNoWebsiteConfirmed') &&
                          window.open(getValues('brand.website'))
                        }
                      />
                    }
                  />
                </Col>
                <Col span={2}>
                  <Checkbox
                    name="brand.isHasNoWebsiteConfirmed"
                    label="No website"
                    highlighted={shouldHighlightField(
                      'isHasNoWebsiteConfirmed',
                    )}
                    onCheckboxChange={isHasNoWebsiteConfirmed => {
                      if (isHasNoWebsiteConfirmed) {
                        setCachedBrandWebsiteValue(getValues('brand.website'))
                        setValue('brand.website', null)
                      }
                      if (!isHasNoWebsiteConfirmed) {
                        setValue('brand.website', cachedBrandWebsiteValue)
                      }
                    }}
                  />
                </Col>
                <Col span={2}>
                  <Select
                    label="Rank"
                    name="brand.rank"
                    disabled={task.brand.rank === MASTER}
                    options={values(BRAND_RANKS)}
                    highlighted={shouldHighlightField('rank')}
                    onSelectChange={rank => {
                      if (rank === SLAVE) {
                        setValue('brand.status', NEW)
                      } else {
                        refetchSimilarMasterBrandsByName()
                        setValue('brand.status', '')
                      }
                    }}
                  />
                </Col>
                <Col span={2}>
                  <FormText label="Messiness" name="brand.messinessScore" />
                </Col>
                <VisibilityContainer isHidden={brandRank !== MASTER}>
                  <Col span={2}>
                    <Select
                      label="Inclusion"
                      name="brand.status"
                      options={inclusionOptions}
                      highlighted={shouldHighlightField('status')}
                      onSelectChange={status => {
                        if (status === INCLUDED) {
                          setValue('brand.exclusionReason', '')
                          setValue('brand.exclusionReasonComment', '')
                        }
                      }}
                    />
                  </Col>
                </VisibilityContainer>
                <VisibilityContainer isHidden={brandRank !== SLAVE}>
                  <VisibilityContainer isHidden={taskStatus !== RESOLVED}>
                    <Col span={5}>
                      <Input
                        name="brand.masterBrand.name"
                        value={`${masterBrand.name} - ${masterBrand.id}`}
                        label="Master brand"
                        disabled
                        addonAfter={
                          <Link
                            to={`/tasks/brand-gateway/${masterBrand.brandGatewayTaskId}?fromSlaveBrandId=${task.brand.id}`}
                            target="_blank"
                          >
                            <Button size="small" shape="circle">
                              <Icon type="link" />
                            </Button>
                          </Link>
                        }
                      />
                    </Col>
                  </VisibilityContainer>
                  <VisibilityContainer isHidden={taskStatus === RESOLVED}>
                    <Col span={5}>
                      <BrandsDropdown
                        name="brand.masterBrand"
                        label="Master brand"
                        includeOnlyMasterBrands
                      />
                    </Col>
                  </VisibilityContainer>
                </VisibilityContainer>
                <VisibilityContainer isHidden={brandStatus !== EXCLUDED}>
                  <Col span={3}>
                    <Select
                      label="Exclusion reason"
                      name="brand.exclusionReason"
                      options={values(BRAND_EXCLUSION_REASONS)}
                      highlighted={shouldHighlightField('exclusionReason')}
                      onSelectChange={exclusionReason => {
                        setValue('brand.exclusionReasonComment', '')

                        if (exclusionReason === INACCESSIBLE_URL) {
                          showWarningNotification({
                            message:
                              'Make sure you checked the website with VPN',
                            description:
                              'Some websites are only accessible with VPN, please check the website with VPN before excluding it.',
                          })
                        }

                        if (exclusionReason === MISC) {
                          setIsExclusionCommentModalOpen(true)
                        }
                      }}
                      style={{ width: '100%' }}
                    />
                  </Col>
                </VisibilityContainer>
                <Col span={2}>
                  <BrandGatewayTaskBrandExclusionReasonComment
                    onClick={() => setIsExclusionCommentModalOpen(true)}
                  />
                </Col>
              </Row>
              <Row>
                <Col span={4}>
                  <FormDate name="brand.modifiedDate" label="Updated at" />
                </Col>
                <Col span={2}>
                  <AntForm.Item label="Actions">
                    <Button onClick={() => setIsCreateBrandModalOpen(true)}>
                      New brand
                      <Icon type="plus" />
                    </Button>
                  </AntForm.Item>
                </Col>
              </Row>
            </Collapse.Panel>
            <Collapse.Panel header="Task" key="task-section">
              <Row gutter={16}>
                <Col span={3}>
                  <AwaitingReasonField
                    valueFieldName="task.awaitingReason"
                    titleFieldName="task.awaitingReasonComment"
                    label="Awaiting reason"
                  />
                </Col>
                <Col span={3}>
                  <FormDate label="Updated at" name="task.modifiedDate" />
                </Col>
                <VisibilityContainer isHidden={taskStatus !== REOPENED}>
                  <>
                    <Col span={3}>
                      <FormText
                        label="Reopened by"
                        name="task.reopenedBy.name"
                      />
                    </Col>
                    <Col span={3}>
                      <FormDate label="Reopened at" name="task.reopenedDate" />
                    </Col>
                    <Col span={5}>
                      <FormText
                        label="Reopen reason"
                        name="task.reopenReason"
                      />
                    </Col>
                  </>
                </VisibilityContainer>
                <Col span={3}>
                  <AntForm.Item label="Miscellaneous task">
                    <MiscTaskVerificationThreeStateSwitch
                      task={watch('task')}
                      onVerifiedButtonClick={onVerifiedButtonClick}
                      onQCVerifiedButtonClick={onQCVerifiedButtonClick}
                      loading={
                        isMarkingMiscTaskAsVerified ||
                        isMarkingMiscTaskAsQCVerified
                      }
                      loggedInUser={loggedInUser}
                    />
                  </AntForm.Item>
                </Col>
              </Row>
            </Collapse.Panel>
          </Collapse>
          <VisibilityContainer isHidden={brandRank !== MASTER}>
            <Panel margin="small no">
              <BrandGatewaySimilarMasterBrandsByName
                brand={taskState.brand}
                similarMasterBrandsByName={similarMasterBrandsByName}
                isLoading={isLoadingSimilarMasterBrandsByName}
                refetchSimilarMasterBrandsByName={
                  refetchSimilarMasterBrandsByName
                }
                refetchSimilarBrands={refetchSimilarBrands}
                isReadonly={!isLoggedInUser && !isAdmin && !isTeamLeader}
              />
              <BrandGatewaySimilarMasterBrandsByLocationWebsite
                brand={taskState.brand}
                similarMasterBrandsByLocationWebsite={
                  similarMasterBrandsByLocationWebsite
                }
                isLoading={isLoadingSimilarMasterBrandsByLocationWebsite}
                refetchSimilarMasterBrandsByLocationWebsite={
                  refetchSimilarMasterBrandsByLocationWebsite
                }
                refetchSimilarBrands={refetchSimilarBrands}
                isReadonly={!isLoggedInUser && !isAdmin && !isTeamLeader}
              />
            </Panel>

            <Panel margin="small no">
              <BrandGatewaySimilarBrandsByName
                brand={taskState.brand}
                isLoading={isLoadingSimilarBrands}
                refetchSimilarBrands={refetchSimilarBrands}
              />
            </Panel>
            <VisibilityContainer
              isHidden={isEmpty(
                taskState.similarBrands?.brandsSimilarByLocationWebsite,
              )}
            >
              <Panel margin="small no">
                <BrandGatewaySimilarBrandsByLocationWebsite
                  isLoading={isLoadingSimilarBrands}
                />
              </Panel>
            </VisibilityContainer>

            <Panel margin="small no">
              <BrandGatewayRelatedBrands
                brandCount={size(taskState.relatedBrands)}
                filteredBrandCount={size(
                  filterRelatedBrandsBySearchTerm(
                    taskState.relatedBrands,
                    relatedBrandsSearchTerm,
                  ),
                )}
                currentPage={relatedBrandsPage}
                onPageChange={onRelatedBrandsPageChange}
                searchTerm={relatedBrandsSearchTerm}
                onSearchChange={onRelatedBrandsSearchChange}
              />
            </Panel>
          </VisibilityContainer>
          <Panel margin="small no">
            <BrandGatewayLocations
              currentBrandId={task.brand.id}
              filterOriginalBrandId={filterOriginalBrandId}
              originalBrands={getOriginalBrands(taskState)}
              onChange={onSelectOriginalBrandChange}
              filteredLocationCount={filteredLocationCount}
              totalLocationCount={size(taskState.locations)}
              currentPage={locationsPage}
              currentPageSize={locationsPageSize}
              onPageChange={onLocationsPageChange}
              allLocationsStatus={allLocationsStatus}
              setAllLocationsStatus={setAllLocationsStatus}
              currentView={currentView}
              filterLocationId={filterLocationId}
              onFilterLocationIdChange={onFilterLocationIdChange}
            />
          </Panel>
          <HiddenInputs
            names={[
              'task.id',
              'task.assignedTo.id',
              'task.assignedTo.name',
              'task.status',
              'task.awaitingReason',
              'task.awaitingReasonComment',
              'task.reopenedDate',
              'task.reopenedBy.id',
              'task.reopenedBy.name',
              'task.reopenReason',
              'task.miscTaskVerifiedBy.id',
              'task.miscTaskVerifiedBy.name',
              'task.miscTaskVerifiedDate',
              'task.miscTaskVerifiedComment',
              'task.miscTaskQCVerifiedBy.id',
              'task.miscTaskQCVerifiedBy.name',
              'task.miscTaskQCVerifiedDate',
              'task.miscTaskQCVerifiedComment',
              'task.currentViewType',
              'task.isAssignable',
              ...flatMap(range(3), index => [
                `task.views[${index}].type`,
                `task.views[${index}].status`,
                `task.views[${index}].resolvedBy.id`,
                `task.views[${index}].resolvedBy.name`,
                `task.views[${index}].resolvedDate`,
              ]),
              'brand.id',
              'brand.exclusionReasonComment',
              'brand.isMasterBrand',
              'brand.slaveBrands',
              'brand.notSameBrands',
            ]}
          />
          <HiddenCheckboxes
            names={['task.isAssigned', 'task.isDone', 'task.isAwaiting']}
          />
        </form>
      </FormProvider>

      <BrandExclusionCommentModal
        exclusionComment={watch('brand.exclusionReasonComment')}
        isModalOpen={isExclusionReasonModalOpen}
        onSave={onExclusionCommentSubmit}
        onCancel={() => setIsExclusionCommentModalOpen(false)}
      />

      <CreateBrandModal
        isModalOpen={isCreateBrandModalOpen}
        website={getValues('brand.website')}
        originalName={task.brand.name}
        closeModal={() => setIsCreateBrandModalOpen(false)}
      />

      <MarkMiscTaskAsVerifiedModal
        isModalOpen={isMarkMiscTaskAsVerifiedModalOpen}
        isQCVerified={isQCVerified}
        comment={
          isQCVerified ? miscTaskQCVerifiedComment : miscTaskVerifiedComment
        }
        onSubmit={handleMarkMiscTaskAsVerified}
        closeModal={() => setIsMarkMiscTaskAsVerifiedModalOpen(false)}
      />
    </>
  )
}

BrandGatewayTaskForm.propTypes = {
  task: PropTypes.object.isRequired,
  isRefetchingTask: PropTypes.bool.isRequired,
  isLoadingSimilarBrands: PropTypes.bool.isRequired,
  similarBrands: PropTypes.object,
  refetchSimilarBrands: PropTypes.func.isRequired,
  isLoadingSimilarMasterBrandsByName: PropTypes.bool.isRequired,
  similarMasterBrandsByName: PropTypes.object.isRequired,
  refetchSimilarMasterBrandsByName: PropTypes.func.isRequired,
  isLoadingSimilarMasterBrandsByLocationWebsite: PropTypes.bool.isRequired,
  similarMasterBrandsByLocationWebsite: PropTypes.object.isRequired,
  refetchSimilarMasterBrandsByLocationWebsite: PropTypes.func.isRequired,
  isSavingTask: PropTypes.bool.isRequired,
  isResolvingTask: PropTypes.bool.isRequired,
  userLockingBrand: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
}

BrandGatewayTaskForm.defaultProps = {
  userLockingBrand: undefined,
  similarBrands: undefined,
}

export default BrandGatewayTaskForm
